import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import {
  doInitLU,
  luHandleAppiError,
  U3208_DEFAULT_ALLOWED_MIME,
  U3208_DEFAULT_MAX_FILE_SIZE_BYTES,
  type U3208InitLU,
} from './U3208Service';
import { U3208ContextProvider } from './U3208Context';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export const NS = 'u3208';

export const U3208ROLES = {
  READ: 'U3208:READ',
  ADD: 'U3208:ADD',
  EDIT: 'U3208:EDIT',
  DELETE: 'U3208:DELETE',
} as const;
export type U3208ROLES = (typeof U3208ROLES)[keyof typeof U3208ROLES];

const defaultInitLU: U3208InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  logicalUnitCode: 'U3208',
  allowedMimeTypes: U3208_DEFAULT_ALLOWED_MIME,
  maxFileSizeBytes: U3208_DEFAULT_MAX_FILE_SIZE_BYTES,
};

const U3208: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3208InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3208'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3208InitLU>({
    doInitLUFn: async () => doInitLU(u2000AxiosAuthenticated),
    onSuccess: (initLU: U3208InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? 'U3208',
        allowedMimeTypes: initLU.allowedMimeTypes?.length
          ? initLU.allowedMimeTypes
          : U3208_DEFAULT_ALLOWED_MIME,
        maxFileSizeBytes: initLU.maxFileSizeBytes ?? U3208_DEFAULT_MAX_FILE_SIZE_BYTES,
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3208ContextProvider initLU={initLUData}>
      <Outlet />
    </U3208ContextProvider>
  );
};

export default U3208;
